import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(res.data);
    } catch {
      toast.error("Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Artikel „${title}" löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`, { withCredentials: true });
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Artikel gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  return (
    <div data-testid="admin-blog-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1E3A8A]">Content</div>
          <h1 className="mt-3 font-heading font-bold text-[#0A192F] text-3xl lg:text-4xl tracking-tight">Blog-Artikel</h1>
          <p className="mt-2 text-slate-600">{posts.length} Beiträge</p>
        </div>
        <Link
          to="/admin/blog/new"
          data-testid="new-post"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A192F] text-white rounded-full text-sm font-semibold hover:bg-[#1E3A8A]"
        >
          <Plus size={16} /> Neuer Artikel
        </Link>
      </div>

      {loading ? (
        <div className="mt-10 p-10 bg-white rounded-2xl border border-slate-200 text-center text-slate-500">Lade…</div>
      ) : posts.length === 0 ? (
        <div className="mt-10 p-10 bg-white rounded-2xl border border-slate-200 text-center text-slate-500">Noch keine Artikel.</div>
      ) : (
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.15em] text-slate-500">
              <tr>
                <th className="text-left px-6 py-4 font-semibold">Titel</th>
                <th className="text-left px-6 py-4 font-semibold">Kategorie</th>
                <th className="text-left px-6 py-4 font-semibold">Autor</th>
                <th className="text-left px-6 py-4 font-semibold">Veröffentlicht</th>
                <th className="text-right px-6 py-4 font-semibold">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {posts.map((p) => (
                <tr key={p.id} data-testid={`post-row-${p.slug}`} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      {p.featured && <Star size={14} className="mt-1 text-amber-500 flex-shrink-0" />}
                      <div>
                        <div className="font-semibold text-[#0A192F]">{p.title}</div>
                        <div className="text-xs text-slate-500 mt-1">/{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{p.category}</td>
                  <td className="px-6 py-4 text-slate-600">{p.author}</td>
                  <td className="px-6 py-4 text-slate-500">{new Date(p.published_at).toLocaleDateString("de-DE")}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/insights/${p.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 text-slate-500 hover:text-[#0A192F]"
                        title="Ansehen"
                        data-testid={`post-view-${p.slug}`}
                      >
                        <ExternalLink size={16} />
                      </Link>
                      <Link
                        to={`/admin/blog/${p.id}/edit`}
                        className="p-2 text-slate-500 hover:text-[#0A192F]"
                        title="Bearbeiten"
                        data-testid={`post-edit-${p.slug}`}
                      >
                        <Pencil size={16} />
                      </Link>
                      <button
                        onClick={() => remove(p.id, p.title)}
                        className="p-2 text-red-500 hover:text-red-700"
                        title="Löschen"
                        data-testid={`post-delete-${p.slug}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
